/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AsyncPipe, DecimalPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader, Sort, SortDirection } from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFooterCell,
    MatFooterCellDef,
    MatFooterRow,
    MatFooterRowDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import { PagingOptions } from '@app/types/PagingOptions';
import { SortOptions } from '@app/types/SortOptions';
import { TableLimits } from '@core/state/appConfig/app-config.model';
import { AppConfigState } from '@core/state/appConfig/app-config.state';
import { ZoomButtonComponent } from '@core/ui/zoomButton/zoom-button.component';
import { FlexAlignDirective, FlexDirective, LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { Store } from '@ngxs/store';
import { BaseFieldComponent } from '@reports/reportContent/fields/base-field.component';
import { ReportContentBaseComponent } from '@reports/reportContent/report-content-base.component';
import { ReportContentFilterComponent } from '@reports/reportContent/report-content-filter/report-content-filter.component';
import { DataFormService } from '@reports/services/data-form.service';
import { IsolatedReportService } from '@reports/services/isolatedReport.service';
import { DataFormLaunchOptions, DataFormModel } from '@reports/state/data-form.model';
import { DisableTableEdit, SetPagingOptions, SetSortOptions } from '@reports/state/report.actions';
import { FilterOptions } from '@reports/state/report.model';
import { ReportState } from '@reports/state/report.state';
import { LoggerService } from '@shared/services/logger.service';
import { isEqual } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'dash-gis-report-content-tabular',
    templateUrl: './report-content-tabular.component.html',
    styleUrls: [ './report-content-tabular.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LayoutDirective,
        LayoutAlignDirective,
        FlexDirective,
        ReportContentFilterComponent,
        MatProgressSpinner,
        FlexAlignDirective,
        FormsModule,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        ZoomButtonComponent,
        MatFooterCellDef,
        MatFooterCell,
        MatSortHeader,
        BaseFieldComponent,
        MatIconButton,
        MatIcon,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatFooterRowDef,
        MatFooterRow,
        MatPaginator,
        MatButton,
        AsyncPipe,
        DecimalPipe
    ]
})
export class ReportContentTabularComponent extends ReportContentBaseComponent implements OnChanges, OnDestroy {
    @Input() public dataFormGroup: UntypedFormGroup;
    @Input() public dataForm: DataFormModel;
    @Input() public tableEditActive: boolean;
    @Input() public mapEnabled: boolean;

    @Output() editRowData = new EventEmitter<DataFormLaunchOptions>();

    // paginator options
    public length: number;
    public pageSizeOptions: TableLimits;
    public pagingOptions$: Observable<PagingOptions>;

    // sort options
    public sortColumn = '';
    public sortDirection: SortDirection = '';
    public sortingOptions$: Observable<SortOptions>;

    paging$: Observable<(id: string, reportName: string) => PagingOptions> = inject(Store).select(ReportState.paging);
    sorting$: Observable<(id: string, reportName: string) => SortOptions> = inject(Store).select(ReportState.sorting);
    filters$: Observable<(id: string, reportName: string) => FilterOptions[]> = inject(Store).select(
        ReportState.filters
    );

    protected columnsToDisplayCache: string[];

    private dispose$ = new Subject<void>();

    private filterOptions$: Observable<FilterOptions[]>;

    constructor(
        logger: LoggerService,
        isolatedReportService: IsolatedReportService,
        private store: Store,
        private dataFormService: DataFormService
    ) {
        super(logger, isolatedReportService);
        this.pageSizeOptions = this.store.selectSnapshot(AppConfigState.tableLimits);
        this.pagingOptions$ = this.paging$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged(isEqual)
        );

        this.sortingOptions$ = this.sorting$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged(isEqual)
        );

        this.filterOptions$ = this.filters$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged(isEqual)
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.dataSource && changes.dataSource.currentValue) {
            if (!isEqual(changes.dataSource.currentValue, changes.dataSource.previousValue)) {
                this.isolatedReportService.reportTrigger$.pipe(takeUntil(this.dataSource.disposing$)).subscribe(() => {
                    this.dataSource.loadReportData();
                });

                this.updateColumnsToDisplay();
            }
        }
    }

    ngOnDestroy(): void {
        this.dispose$.next();
    }

    pageChange(evt: PageEvent) {
        this.store.dispatch(
            new SetPagingOptions({
                id: this.id,
                reportName: this.definition.name,
                pagingOptions: {
                    page: evt.pageIndex,
                    limit: evt.pageSize
                }
            })
        );
    }

    sortChange(evt: Sort) {
        this.store.dispatch(
            new SetSortOptions({
                id: this.id,
                reportName: this.definition.name,
                sortOptions: {
                    column: evt.active,
                    direction: evt.direction
                }
            })
        );
    }

    getTotalsCellDisplay(fieldName: string, index: number): string {
        if (!(this.mapEnabled && this.definition.enableZoom) && index === 0) {
            return 'label';
        }

        if (this.definition.totalFields) {
            if (-1 !== this.definition.totalFields.findIndex((f) => f === fieldName)) {
                return 'value';
            }
        }
    }

    getAveragesCellDisplay(fieldName: string, index: number): string {
        if (this.definition.averageFields) {
            if (!(this.mapEnabled && this.definition.enableZoom) && index === 0) {
                return 'label';
            }

            if (-1 !== this.definition.averageFields.findIndex((f) => f === fieldName)) {
                return 'value';
            }
        }
    }

    getTotalsValue$(fieldName: string): Observable<number> {
        return this.dataSource.reportTotals$.pipe(
            filter((arr) => arr.length > 0),
            map((totals) => {
                const total = totals.find((t) => t.name === fieldName);
                return total.value;
            })
        );
    }

    getAveragesValue$(fieldName: string): Observable<number> {
        return this.dataSource.reportAverages$.pipe(
            filter((arr) => arr.length > 0),
            map((averages) => {
                const average = averages.find((t) => t.name === fieldName);
                return average.value;
            })
        );
    }

    tableEditSave() {
        const formData = this.dataFormGroup.value;
        this.dataFormService
            .saveTableData(this.definition.tableDataFormName, formData)
            .pipe(
                concatMap(() =>
                    this.store.dispatch(
                        new DisableTableEdit({
                            id: this.id,
                            reportName: this.definition.name
                        })
                    )
                )
            )
            .subscribe(() => {
                this.isolatedReportService.triggerReport();
            });
    }

    tableEditCancel() {
        this.store.dispatch(
            new DisableTableEdit({
                id: this.id,
                reportName: this.definition.name
            })
        );
    }

    protected updateColumnsToDisplay() {
        const columns = this.definition.fields.filter((f) => !f.hidden).map((f) => f.name);
        if (this.mapEnabled && this.definition.enableZoom) {
            // zoom icon/button goes at front of the row
            columns.unshift('zoom');
        }
        if (this.definition.rowDataFormName) {
            // edit icon goes at the end of the row
            columns.push('edit');
        }
        this.columnsToDisplayCache = columns;
    }

    get shouldDisplayFilterUi(): boolean {
        // only display filters if at least one filter is not hidden
        return this.definition.filters.reduce((result, current) => result || !current.hidden, false);
    }

    get columnsToDisplay(): string[] {
        return this.columnsToDisplayCache;
    }

    get totalsColumnsToDisplay(): string[] {
        return this.columnsToDisplay.map((name) => `total_${ name }`);
    }

    get averagesColumnsToDisplay(): string[] {
        return this.columnsToDisplay.map((name) => `average_${ name }`);
    }
}
